import React, {useCallback, useEffect, useState} from "react";
import Dialog from "../../base/dialog";
import {Col, Row} from "reactstrap";
import {DialogContent, DialogTitle, Divider} from "@mui/material";
import Button from "../../base/button/button";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import CustomerMemberAutocomplete from "../../text-fields/autocomplete/customer-member-autocomplete";

const TestNotificationDialog = ({open, setOpen, values}) => {
    const [users, setUsers] = useState([])
    const [sending, setSending] = useState(false)
    const [usersError, setUsersError] = useState(null)
    const isMounted = useIsMounted()

    /**
     * Resets the users and the error message when the dialog is closed.
     */
    useEffect(() => {
        if (!open) {
            setUsers([])
            setUsersError(null)
        }
    }, [open])

    /**
     * When the users are changed, resets the error message.
     */
    useEffect(() => {
        if (users?.length) {
            setUsersError(null)
        }
    }, [users?.length])

    /**
     * Sends a test notification to the selected users.
     * @type {(function(): Promise<void>)}
     */
    const sendTestNotification = useCallback(async () => {
        if (!users?.length) {
            setUsersError("At least one user must be selected.")
            return
        }
        const _values = values ?? {}
        _values['targetUserId'] = users.map(e => e.id.toString())
        _values['sendToAll'] = false;
        setSending(true)
        await Api.sendNotification(_values);
        if (!isMounted())
            return
        setSending(false)
    }, [users, values, isMounted])

    return (
        <Dialog
            open={open}
            setOpen={setOpen}
            maxWidth={'sm'}
        >
            <DialogTitle>
                <p className={`text-lg text-color-1 my-2 font-weight-semi-bold text-center mb-3`}>
                    Send a Test Notification
                </p>
                <Divider className='divider'/>
            </DialogTitle>
            <DialogContent>
                <Col xs={12} className={'mb-4 w-100'}>
                    <p className={'text-color-3 text-sm'}>
                        You can send a test notification to test and preview a sample of it before sending it to
                        your users.
                    </p>
                </Col>
                <Col xs={12} className='px-2 mb-2'>
                    <CustomerMemberAutocomplete
                        label={'Target Users'}
                        multiple
                        filterSelectedOptions
                        textFieldProps={{
                            error: !!usersError,
                            helperText: usersError
                        }}
                        value={users}
                        onChange={(_, users) => setUsers(users)}
                    />
                </Col>
            </DialogContent>
            <Row className='no-gutters justify-content-center align-items-start w-100'>
                <div className='my-2 w-100'/>
                <Divider className='divider'/>
                <Row className='no-gutters justify-content-between align-items-start w-100'>
                    <Button
                        disabled={!open || sending}
                        variant={'dialog'}
                        color={'primary'}
                        onClick={() => setOpen(false)}
                        borderRadius={'0 8px 0 8px'}
                    >
                        Close
                    </Button>
                    <Button
                        disabled={!open || sending}
                        variant={'dialog'}
                        color={'primary'}
                        onClick={sendTestNotification}
                        borderRadius={'8px 0 8px 0'}
                    >
                        {
                            sending
                                ? "Sending..."
                                : "Send"
                        }
                    </Button>
                </Row>
            </Row>
        </Dialog>
    );
}


export default TestNotificationDialog;
